// packages/shared/src/components/UI/Button/Button.web.tsx
// Zero-React-type version (no DOM generics) so it compiles in any TS/Babel setup.

import React, { useState } from 'react';
import { colors } from '../../../theme/colors';
import { ButtonProps } from './Button.types';

// Keep props minimal and untyped to avoid DOM/React TS issues.
// You still get `type`, `onClick`, `style`, `className`, data-*, aria-*, etc.
type AppButtonProps = ButtonProps & {
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: any) => void;
  onPress?: (e: any) => void;
  style?: any;
  className?: string;
  testID?: string;
  // allow anything else to flow through
  [key: string]: any;
};

function baseStyle(disabled?: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 4,
    boxSizing: 'border-box',
    fontFamily:
      '"SF Pro Text","SF Pro Icons","AOS Icons","Helvetica Neue",Helvetica,Arial,sans-serif',
    fontSize: 17,
    fontWeight: 400,
    letterSpacing: -0.022,
    lineHeight: 1.47059,
    minWidth: 30,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
    whiteSpace: 'nowrap',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.3 : 1,
    outline: 'none',
    transition: 'box-shadow 120ms ease',
    borderWidth: 1,
    borderStyle: 'solid',
  } as const;
}

function sizeStyle(size: ButtonProps['size'] = 'md') {
  if (size === 'sm') return { padding: '4px 12px', fontSize: 15 };
  if (size === 'lg') return { padding: '6px 18px', fontSize: 18 };
  return { padding: '4px 15px' }; // md
}

function variantStyle(
  variant: NonNullable<ButtonProps['variant']>,
  state: 'default' | 'hover' | 'active'
) {
  if (variant === 'outline') {
    return {
      background: 'white',
      color: colors.BLUE_BOTTOM,
      borderColor: colors.BLUE_BORDER,
      ...(state === 'hover'
        ? { boxShadow: 'rgba(131,192,253,.5) 0 0 0 3px' }
        : {}),
      ...(state === 'active' ? { filter: 'brightness(.98)' } : {}),
    };
  }
  if (variant === 'ghost') {
    return {
      background: 'transparent',
      color: colors.BLUE_BOTTOM,
      borderColor: 'transparent',
      ...(state === 'hover' ? { background: 'rgba(0,112,201,0.06)' } : {}),
      ...(state === 'active' ? { background: 'rgba(0,112,201,0.12)' } : {}),
    };
  }
  if (variant === 'danger') {
    const top = state === 'active' ? colors.RED_BOTTOM : colors.RED_TOP;
    const bottom = colors.RED_BOTTOM;
    return {
      color: '#fff',
      backgroundImage: `linear-gradient(${top}, ${bottom})`,
      borderColor: colors.RED_BORDER,
    };
  }
  const top =
    state === 'hover'
      ? colors.BLUE_HOVER_TOP
      : state === 'active'
        ? colors.BLUE_ACTIVE_TOP
        : colors.BLUE_TOP;
  const bottom =
    state === 'hover'
      ? colors.BLUE_HOVER_BOTTOM
      : state === 'active'
        ? colors.BLUE_ACTIVE_BOTTOM
        : colors.BLUE_BOTTOM;
  const border =
    state === 'hover'
      ? colors.BLUE_HOVER_BORDER
      : state === 'active'
        ? colors.BLUE_ACTIVE_BORDER
        : colors.BLUE_BORDER;
  return {
    color: '#fff',
    backgroundImage: `linear-gradient(${top}, ${bottom})`,
    borderColor: border,
  };
}

export default function Button({
  children,
  onPress,
  disabled,
  loading,
  variant = 'solid',
  size = 'md',
  leftIcon,
  rightIcon,
  testID,
  type,
  onClick,
  style: externalStyle,
  ...rest
}: AppButtonProps) {
  const [isHover, setHover] = useState(false);
  const [isActive, setActive] = useState(false);
  const state: 'default' | 'hover' | 'active' = isActive
    ? 'active'
    : isHover
      ? 'hover'
      : 'default';

  const style = {
    ...baseStyle(disabled || loading),
    ...sizeStyle(size),
    ...variantStyle(variant ?? 'solid', state),
    ...(externalStyle || {}),
  };

  const handleClick = (e: any) => {
    if (disabled || loading) {
      if (e && e.preventDefault) e.preventDefault();
      return;
    }
    if (onPress) onPress(e);
    if (onClick) onClick(e);
  };

  return (
    <button
      type={type ?? 'button'}
      data-testid={testID}
      disabled={disabled || loading}
      onClick={handleClick}
      {...rest} // forward native props (className, aria-*, data-*, etc.)
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
    >
      {loading ? (
        <span>Loading…</span>
      ) : (
        <>
          {leftIcon ? (
            <span style={{ display: 'inline-flex', marginRight: 4 }}>
              {leftIcon}
            </span>
          ) : null}
          <span>{children}</span>
          {rightIcon ? (
            <span style={{ display: 'inline-flex', marginLeft: 4 }}>
              {rightIcon}
            </span>
          ) : null}
        </>
      )}
    </button>
  );
}
